import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2, Mail, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { printPricedOrder, printProPdf, pricedOrderUrl } from "@/lib/printPdf";
import { ProCatalogueProductForm } from "./ProCatalogueProductForm";

const API = process.env.REACT_APP_BACKEND_URL;
const field = "h-11 w-full rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gold)]";
const btn = "inline-flex h-11 items-center gap-2 rounded-full px-5 text-xs font-semibold uppercase tracking-[0.18em] transition-colors disabled:opacity-50";
const mo = (n) => (n ? `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} Mo` : "—");

const PRINTS = [["short", "Imprimer le catalogue 5 pages"], ["flyer", "Imprimer le flyer recto-verso"], ["print", "Imprimer le catalogue complet"], ["order", "Imprimer le bon de commande (avec tarifs)"]];

const openPricedOrder = async () => {
  const w = window.open("", "_blank");
  try { const url = await pricedOrderUrl(); if (w) w.location.href = url; else window.location.href = url; } catch (e) { if (w) w.close(); toast.error(errMsg(e)); }
};

function PrintRow() {
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-4 flex flex-wrap gap-3">
      {PRINTS.map(([kind, label]) => (
        <button key={kind} data-testid={`pro-cat-print-${kind}`} type="button" disabled={!!busy} onClick={() => (kind === "order" ? printPricedOrder(setBusy).catch((e) => toast.error(errMsg(e))) : printProPdf(kind, setBusy))}
          className={`${btn} border border-[var(--gold)]/70 text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]`}>
          {busy === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}{label}
        </button>
      ))}
    </div>
  );
}

function PdfBox({ data, onDone }) {
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState("pchaubo@hotmail.fr");
  const [sending, setSending] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/pro-catalogue/generate"); toast.success(`Version ${r.data.version} générée`); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Envoyer le lien du catalogue à ${to} ?`)) return;
    setSending(true);
    try { await api.post("/admin/pro-catalogue/send", { to }); toast.success("Email envoyé (remis au fournisseur)"); } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };
  return (
    <div data-testid="pro-cat-pdf-box" className="rounded-sm border border-[var(--gold)]/50 bg-[var(--ink-2)] p-6">
      <p className="eyebrow">PDF du catalogue</p>
      <p data-testid="pro-cat-pdf-status" className="mt-2 text-sm text-[var(--parchment)]/85">
        {data.version ? `Version ${data.version} · ${data.generated_at ? new Date(data.generated_at).toLocaleString("fr-FR") : ""} · impression ${mo(data.sizes?.print)} · téléphone ${mo(data.sizes?.mobile)} · 5 pages ${mo(data.sizes?.short)}` : "Pas encore généré"}
        {data.dirty ? " · modifications en attente : le PDF sera régénéré au prochain téléchargement" : ""}
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button data-testid="pro-cat-generate" onClick={gen} disabled={busy} className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Générer une nouvelle version</button>
        <a data-testid="pro-cat-open-print" href={`${API}/api/pro-catalogue/pdf/print?inline=1`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />PDF impression</a>
        <a data-testid="pro-cat-open-mobile" href={`${API}/api/pro-catalogue/pdf/mobile?inline=1`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />PDF téléphone</a>
        <a data-testid="pro-cat-open-short" href={`${API}/api/pro-catalogue/pdf/short?inline=1`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />Version 5 pages</a>
        <button type="button" data-testid="pro-cat-open-order-priced" onClick={openPricedOrder} className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />Bon de commande avec tarifs</button>
        <a data-testid="pro-cat-open-order" href={`${API}/api/pro-catalogue/pdf/order?inline=1`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />Bon de commande sans prix</a>
        <a data-testid="pro-cat-open-flyer" href={`${API}/api/pro-catalogue/pdf/flyer?inline=1`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}><Download className="h-4 w-4" />Flyer recto-verso</a>
        <a data-testid="pro-cat-open-page" href="/catalogue-pro" target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] hover:border-[var(--gold)]`}>Page à partager</a>
      </div>
      <PrintRow />
      <form onSubmit={send} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <input data-testid="pro-cat-send-to" type="email" required value={to} onChange={(e) => setTo(e.target.value)} className={field} />
        <button data-testid="pro-cat-send" disabled={sending} className={`${btn} shrink-0 border border-[var(--gold)]/70 text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]`}>{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}Envoyer par email</button>
      </form>
    </div>
  );
}

function SettingsBox({ data, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ contacts: data.contacts, site_url: data.site_url, pro_url: data.pro_url, edition: data.edition, year: data.year, cover_image: data.cover_image || "", categories: data.categories }); }, [data]);
  if (!f) return null;
  const setContact = (i, k) => (e) => setF({ ...f, contacts: f.contacts.map((c, j) => (j === i ? { ...c, [k]: e.target.value } : c)) });
  const setCat = (i, k) => (e) => setF({ ...f, categories: f.categories.map((c, j) => (j === i ? { ...c, [k]: e.target.value } : c)) });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/admin/pro-catalogue/settings", f); toast.success("Réglages enregistrés"); onDone(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="pro-cat-settings" onSubmit={save} className="space-y-5 rounded-sm border border-[var(--line)] p-6">
      <p className="eyebrow">Contacts, liens et catégories</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {f.contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-2 gap-2">
            <input data-testid={`pro-cat-contact-name-${i}`} value={c.name} onChange={setContact(i, "name")} className={field} aria-label="Nom du contact" />
            <input data-testid={`pro-cat-contact-phone-${i}`} value={c.phone} onChange={setContact(i, "phone")} className={field} aria-label="Téléphone" />
          </div>
        ))}
        <input data-testid="pro-cat-site-url" value={f.site_url} onChange={(e) => setF({ ...f, site_url: e.target.value })} className={field} aria-label="Site produits" />
        <input data-testid="pro-cat-pro-url" value={f.pro_url} onChange={(e) => setF({ ...f, pro_url: e.target.value })} className={field} aria-label="Site professionnel" />
        <input data-testid="pro-cat-edition" value={f.edition} onChange={(e) => setF({ ...f, edition: e.target.value })} className={field} aria-label="Édition" />
        <input data-testid="pro-cat-year" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} className={field} aria-label="Année" />
      </div>
      <div className="space-y-3">
        {f.categories.map((c, i) => (
          <div key={c.key} className="grid gap-2 md:grid-cols-[220px_1fr]">
            <input data-testid={`pro-cat-category-title-${c.key}`} value={c.title} onChange={setCat(i, "title")} className={field} aria-label="Titre de la catégorie" />
            <input data-testid={`pro-cat-category-intro-${c.key}`} value={c.intro} onChange={setCat(i, "intro")} className={field} aria-label="Texte d'ouverture" />
          </div>
        ))}
      </div>
      <button data-testid="pro-cat-settings-save" disabled={busy} className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>Enregistrer les réglages</button>
    </form>
  );
}

function ProductsList({ data, onEdit, onDelete }) {
  const title = Object.fromEntries((data.categories || []).map((c) => [c.key, c.title]));
  return (
    <div className="overflow-x-auto">
      <table data-testid="pro-cat-products" className="w-full min-w-[760px] text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]"><th className="p-2">Photo</th><th className="p-2">Référence</th><th className="p-2">Catégorie</th><th className="p-2">Format</th><th className="p-2">Conditionnement</th><th className="p-2" /></tr></thead>
        <tbody>
          {(data.products || []).map((p) => (
            <tr key={p.slug} data-testid={`pro-cat-row-${p.slug}`} className={`border-t border-[var(--line)] ${p.active ? "" : "opacity-50"}`}>
              <td className="p-2">{p.image ? <img src={imgUrl(p.image)} alt="" className="h-12 w-12 object-cover" /> : <span className="text-xs text-amber-300">Photo manquante</span>}</td>
              <td className="p-2">{p.name}</td>
              <td className="p-2">{title[p.category] || p.category}</td>
              <td className="p-2">{p.format || <span className="text-xs text-amber-300">À compléter</span>}</td>
              <td className="p-2">{p.on_request || !p.packaging ? "Sur demande" : p.packaging}</td>
              <td className="p-2 text-right">
                <button data-testid={`pro-cat-edit-${p.slug}`} onClick={() => onEdit(p)} className="p-2 hover:text-[var(--gold)]" aria-label="Modifier"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`pro-cat-delete-${p.slug}`} onClick={() => onDelete(p)} className="p-2 hover:text-red-400" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProCatalogueAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-pro-catalogue"], queryFn: async () => (await api.get("/admin/pro-catalogue")).data });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-pro-catalogue"] });
  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />;
  if (q.isError || !q.data) return <p data-testid="pro-cat-error" className="text-sm">{errMsg(q.error)}</p>;
  const save = async (body) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/pro-catalogue/products/${editing.slug}`, body); else await api.post("/admin/pro-catalogue/products", body);
      toast.success("Référence enregistrée"); setOpen(false); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Retirer « ${p.name} » du catalogue ?`)) return;
    try { await api.delete(`/admin/pro-catalogue/products/${p.slug}`); toast.success("Référence retirée"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="pro-cat-admin" className="space-y-8">
      <PdfBox data={q.data} onDone={refresh} />
      <div className="flex items-center justify-between"><p className="eyebrow">{(q.data.products || []).length} références</p>
        <button data-testid="pro-cat-add" onClick={() => { setEditing(null); setOpen(true); }} className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}><Plus className="h-4 w-4" />Ajouter une référence</button>
      </div>
      <ProductsList data={q.data} onEdit={(p) => { setEditing(p); setOpen(true); }} onDelete={del} />
      <SettingsBox data={q.data} onDone={refresh} />
      <ProCatalogueProductForm open={open} onOpenChange={setOpen} initial={editing} categories={q.data.categories || []} onSave={save} saving={saving} />
    </div>
  );
}
